import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { auth } from '../firebase';
import { openModal } from '../store/modalSlice';
import { deviceSecurityAction } from '../utils/deviceSecurity';

// Opening uses the shared Redux modal. A server deferral is only a reminder
// preference; it never establishes trust or bypasses the required policy.
export default function useDeviceSecurity({ userId, enabled }) {
  const dispatch = useDispatch();
  const modal = useSelector(state => state.modals);
  const [security, setSecurity] = useState(null);
  const prompted = useRef(null);
  const manualPrompted = useRef(null);

  useEffect(() => {
    if (!enabled || !userId || auth.currentUser?.uid !== userId) return;
    let active = true;
    let pending = false;
    let manualRequested = false;
    const controller = new AbortController();
    const check = async event => {
      if (event?.type === 'node:open-device-security') manualRequested = true;
      if (pending || (event?.type === 'visibilitychange' && document.visibilityState !== 'visible')) return;
      pending = true;
      try {
        const next = await deviceSecurityAction('status', {}, { userId, signal: controller.signal });
        if (!active) return;
        setSecurity({ ...next, userId, manual: manualRequested, requestId: Date.now() });
        manualRequested = false;
      } catch (_) {
        // A failure to load an optional reminder must not interrupt a session.
        // Required access continues to be enforced by the server.
      } finally { pending = false; }
    };
    setSecurity(null);
    check();
    window.addEventListener('online', check);
    window.addEventListener('node:open-device-security', check);
    window.addEventListener('node:device-security-required', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      active = false;
      controller.abort();
      window.removeEventListener('online', check);
      window.removeEventListener('node:open-device-security', check);
      window.removeEventListener('node:device-security-required', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [userId, enabled]);

  useEffect(() => {
    if (!enabled || !security || security.userId !== userId || modal.isOpen) return;
    if (security.manual) {
      if (manualPrompted.current === security.requestId) return;
      manualPrompted.current = security.requestId;
    } else {
      if (security.verified) return;
      if (security.mode !== 'required' && (security.deferredUntil > Date.now() || prompted.current === userId)) return;
    }
    prompted.current = userId;
    dispatch(openModal({ modalType: 'deviceSecurity', modalProps: { initialSecurity: security, userId } }));
  }, [dispatch, enabled, security, userId, modal.isOpen]);
}
